import { AvailabilityState, IProduktDetailsVerfuegbarkeit, IProduktVerfuegbarkeit, Product, TextNodeType } from '@mwe/models';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit, output, signal } from '@angular/core';
import { capitalizeString, containsProductCategory, everyTrue, isEqualCategoryString, isProductCategory } from '@mwe/utils';
import { ProductCategoryEnum } from '@mwe/constants';
import { EnvironmentService, RelocationService } from '@mwe/services';
import { CardSelectableComponent } from '../../cards/card-selectable/card-selectable.component';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { HeadlineService } from '../../headline/headline.service';
import { CorrelatedCompanyReferencePipe, TranslateTariffClassPipe } from '@mwe/ui/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'mwe-wizard-product-card-container',
  templateUrl: './wizard-product-card-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardSelectableComponent,
    ProductIconComponent,
    CorrelatedCompanyReferencePipe,
    TranslatePipe,
    TranslateTariffClassPipe,
    AsyncPipe,
  ],
})
export class WizardProductCardContainerComponent implements OnInit {
  product = input.required<Product>();
  productAvailabilities = input<IProduktVerfuegbarkeit[]>([]);
  isAvailableAtNewAddress = input<boolean>(false);
  wasInitiallyOrPreviouslyChecked = input<boolean>(false);
  showCurrentProductTariff = input<boolean>(false);
  showTariffClasses = input<boolean>(false);
  isDisabled = input<boolean>(false);
  isRelocationProcess = input(false);
  isOrdered = model<boolean>(false);
  headingNodeType = input<TextNodeType>('h2');
  readonly toggleState = output<boolean>();
  productCategory = computed(() => this.product().category);
  showTVInfoText = computed(() => {
    return (
      !this.isRelocationProcess() &&
      this.isDisabled() &&
      (isProductCategory(this.productCategory(), ProductCategoryEnum.INTERNET) ||
        isProductCategory(this.productCategory(), ProductCategoryEnum.IPTV))
    );
  });

  showInformationText = signal(false);
  availabilityDetails = computed<IProduktDetailsVerfuegbarkeit[]>(() => {
    if (isProductCategory(this.productCategory(), ProductCategoryEnum.FERNWAERME)) {
      return (
        this.productAvailabilities()?.find(element => isProductCategory(element.sparte, ProductCategoryEnum.FERNWAERME))?.details ?? []
      );
    }

    return [];
  });
  informationTextProductCategory = computed(() => capitalizeString(this.productCategory()));
  containerTestSelector = computed(() => `wizard-product-card-container-${this.productCategory()?.toLowerCase()}`);
  private relocationService = inject(RelocationService);
  private environmentService = inject(EnvironmentService);
  headlineService = inject(HeadlineService);
  tariffClasses = computed<string[]>(() => {
    const customerType = this.environmentService.getPortalCustomerType();

    if (everyTrue(!this.showCurrentProductTariff(), this.isAvailableAtNewAddress())) {
      if (
        isProductCategory(this.productCategory(), ProductCategoryEnum.STROM) ||
        isProductCategory(this.productCategory(), ProductCategoryEnum.GAS)
      ) {
        return [this.relocationService.getConfigTariffs()?.[this.productCategory().toLowerCase()]?.[customerType]?.name];
      }

      if (isProductCategory(this.productCategory(), ProductCategoryEnum.FERNWAERME) && this.availabilityDetails()) {
        return this.availabilityDetails().map(element => capitalizeString(element?.lieferkomponente));
      }

      return [];
    }
    return this.product().tariffClasses;
  });
  fakeProductForTariffClassTranslations = computed<Product>(() => {
    return {
      category: this.productCategory(),
      tariffClasses: this.tariffClasses(),
    } as Product;
  });

  ngOnInit() {
    this.initializeComponentData();
  }

  onProductSelect(isChecked: boolean) {
    this.isOrdered.set(isChecked);
    this.toggleState.emit(isChecked);
  }

  private initializeComponentData() {
    this.headlineService.setReferenceHeadline(this.headingNodeType());
    this.onProductSelect(this.wasInitiallyOrPreviouslyChecked());
    this.showInformation(this.product());
  }

  private showInformation(product: Product) {
    this.showInformationText.set(false);

    const sameCategoryAvailableProduct = this.productAvailabilities()?.find(
      p =>
        containsProductCategory(product.category, [ProductCategoryEnum.STROM, ProductCategoryEnum.GAS, ProductCategoryEnum.FERNWAERME]) &&
        isEqualCategoryString(p.sparte, product.category),
    );

    if (!sameCategoryAvailableProduct) return;

    this.showInformationText.set(
      sameCategoryAvailableProduct.verfuegbar === AvailabilityState.UNKNOWN ||
        sameCategoryAvailableProduct.verfuegbar === AvailabilityState.TECHNICAL_ERROR ||
        sameCategoryAvailableProduct.verfuegbar === AvailabilityState.BUILDING,
    );
  }
}
